import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { hasFinePointer } from '@/lib/pointer'
import { ExploreFilterRow } from '@/packages/explore/components/ExploreFilterRow'
import { ExploreGroup } from '@/packages/explore/components/ExploreGroup'
import { ExploreHighlighted } from '@/packages/explore/components/ExploreHighlighted'
import { ExploreSearchRow } from '@/packages/explore/components/ExploreSearchRow'
import {
  EXPLORE_FILTERS,
  HIGHLIGHTED_PRODUCTS,
  PRODUCT_GROUPS,
} from '@/packages/explore/data/explore.mock'
import { useLocalize } from '@/packages/explore/lib/localize'
import type { ExploreLayout, ProductGroup } from '@/packages/explore/types'

interface ExplorePanelProps {
  open: boolean
  onClose: () => void
}

function matches(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.trim().toLowerCase())
}

/**
 * Explore — Figma 2267:290683 (1440 grid) · 290882 (1440 list) · 290981 /
 * 291381 (1200) · 291105 / 291538 (900) · 291223 / 291631 (600) · 291870 /
 * 292080 (400).
 *
 * From 900px up the panel is the 700×440 card the frames draw, centred over a
 * dimmed page; below that it fills the viewport and the close button moves to
 * its own row. The card clips, so the content scrolls inside it.
 */
export function ExplorePanel({ open, onClose }: ExplorePanelProps): ReactElement | null {
  const { t } = useTranslation()
  const localize = useLocalize()

  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<string | null>(null)
  const [layout, setLayout] = useState<ExploreLayout>('grid')
  const inputRef = useRef<HTMLInputElement>(null)

  // Escape closes the panel.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    globalThis.addEventListener('keydown', onKeyDown)
    return () => {
      globalThis.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onClose])

  useEffect(() => {
    if (!open || !hasFinePointer()) return
    inputRef.current?.focus()
  }, [open])

  // A fresh search every time it opens.
  useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const groups = useMemo<ProductGroup[]>(() => {
    const byFilter = filter ? PRODUCT_GROUPS.filter((group) => group.id === filter) : PRODUCT_GROUPS
    if (!query.trim()) return byFilter

    return byFilter
      .map((group) => ({
        ...group,
        products: group.products.filter((product) => matches(localize(product.label), query)),
      }))
      .filter((group) => group.products.length > 0)
  }, [filter, query, localize])

  const highlighted = useMemo(
    () => HIGHLIGHTED_PRODUCTS.filter((product) => matches(localize(product.label), query)),
    [query, localize],
  )

  const close = useCallback(() => {
    onClose()
  }, [onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center">
      <button
        type="button"
        aria-label={t('actions.close')}
        onClick={close}
        className="bg-overlay absolute inset-0 cursor-default"
      />

      <div
        data-node-id="2267:290712"
        data-name="explore"
        role="dialog"
        aria-modal="true"
        aria-label={t('explore.title')}
        className="hub-surface-explore relative flex h-dvh w-screen flex-col overflow-clip md:h-110 md:w-175 md:rounded-[12px]"
      >
        <ExploreSearchRow ref={inputRef} value={query} onChange={setQuery} onClose={close} />

        <div
          data-node-id="2267:290722"
          data-name="content"
          className="flex min-h-px w-full flex-1 flex-col items-start gap-4 overflow-y-auto px-4 pt-3 pb-4"
        >
          <ExploreFilterRow
            nodeId="2267:290723"
            filters={EXPLORE_FILTERS}
            selected={filter}
            onSelect={setFilter}
            layout={layout}
            onLayoutChange={setLayout}
          />

          {highlighted.length > 0 && (
            <ExploreHighlighted nodeId="2267:290735" products={highlighted} onNavigate={close} />
          )}

          {groups.map((group) => (
            <ExploreGroup key={group.id} group={group} layout={layout} onNavigate={close} />
          ))}

          {groups.length === 0 && highlighted.length === 0 && (
            <p className="hub-label-md text-widget-secondary">{t('explore.noResults')}</p>
          )}
        </div>
      </div>
    </div>
  )
}
